package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.*
import com.sddamico.models.LeagueYearDetailResponse
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

val DashboardGridStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).gap(1.5.cssRem).fillMaxWidth() }
}

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home", requiresAuth = false))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    var leagueData by remember { mutableStateOf<LeagueYearDetailResponse?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            leagueData = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
        } catch (e: ApiException) {
            error = e.errorMessage
        } catch (e: Exception) {
            error = "Failed to load league data"
        }
        loading = false
    }

    if (loading) {
        PageLoading()
        return
    }

    if (error != null) {
        ErrorCard(error!!) {
            // Retry would need to re-trigger the effect; for now, show the error
        }
        return
    }

    val data = leagueData
    if (data == null) {
        EmptyState("Season hasn't started yet")
        return
    }

    Div(DashboardGridStyle.toAttrs()) {
        // Left sidebar - standings
        Column(Modifier.gap(1.5.cssRem)) {
            StandingsPanel(data.standings, data.lastSyncedAt)
        }

        // Right content - tournament cards
        Column(Modifier.gap(1.5.cssRem)) {
            // Active tournaments first (exclude unscored — e.g. non-major women's tennis)
            val isScoredTournament = { t: com.sddamico.models.TournamentRefResponse ->
                t.isScored && !(t.sport == "WOMENS_TENNIS" && t.importance != "MAJOR")
            }
            val activeTournaments = data.tournaments.filter { it.status == "ACTIVE" && isScoredTournament(it) }
            val completedTournaments = data.tournaments.filter { it.status == "COMPLETED" && isScoredTournament(it) }
            val upcomingTournaments = data.tournaments.filter { it.status == "FUTURE" }

            if (activeTournaments.isEmpty() && completedTournaments.isEmpty()) {
                EmptyState(
                    if (upcomingTournaments.isNotEmpty()) {
                        "No tournaments in progress. Next: ${upcomingTournaments.last().name}"
                    } else {
                        "No tournaments scheduled"
                    }
                )
            }

            if (activeTournaments.isNotEmpty()) {
                SpanText(
                    "In Progress",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )
                activeTournaments.forEach { tournament ->
                    TournamentCard(tournament, showResultsLink = true)
                }
            }

            if (completedTournaments.isNotEmpty()) {
                SpanText(
                    "Completed",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                        .margin(top = if (activeTournaments.isNotEmpty()) 1.cssRem else 0.px)
                )
                completedTournaments.take(5).forEach { tournament ->
                    TournamentCard(tournament, showResultsLink = true)
                }
            }
        }
    }
}
